import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { EyeIcon, EyeCloseIcon } from "../../icons";
import { useAuth } from "../../contexts/useAuth";
import { isNativePlatform } from "../../config/platform";
import { safeNextPath } from "../../config/loginRedirect";
import { pillButtonClass, pillFieldClass, pillInputClass } from "./authControls";

/**
 * Sign-in, on the brand design (Claude Design `Login D` / `Login Mobile`): pill fields with a
 * leading icon, a right-aligned forgot link, and the dark-green LOGIN pill. Only the presentation
 * changed — the login call, the confirm-your-email resend flow, remember-me and the native-app
 * behaviour are the same as before.
 *
 * The design draws no "keep me signed in" control, but it is real behaviour (30-day persistence,
 * and the native app forces it on), so it stays — as a compact checkbox on the same row as the
 * forgot link rather than a row of its own.
 */

export default function SignInForm() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const isMobile = isNativePlatform();
    const [rememberMe, setRememberMe] = useState(true); // Default to true so token is stored in localStorage
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { login } = useAuth();

    /**
     * Where to go once signed in. An emailed team invitation sends people here with
     * `?next=/accept-invitation?token=…`, and an ended session with `?next=<the page they were on>`.
     * safeNextPath only follows a path on this site — anything else (another origin, `/\evil.com`,
     * a tab or newline trick, `javascript:`) would make this an open redirect that phishes our own
     * users — and otherwise gives /dashboard (NOT "/", which is the public marketplace now).
     */
    const destination = safeNextPath(searchParams.get('next'));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null | React.ReactNode>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setError(null);

        if (!email || !password) {
            setError("Please enter both email and password");
            return;
        }

        setLoading(true);

        try {
            // On mobile, always use persistent login
            const effectiveRememberMe = isMobile ? true : rememberMe;

            // ONE request per submit. Every attempt counts toward brute-force protection, so a
            // failure is shown as-is rather than retried (the old fallback re-POSTed every
            // failed sign-in, which locked people out after four typos).
            const result = await login(email, password, effectiveRememberMe, isMobile);

            if (result.ok) {
                navigate(destination);
                return;
            }

            // An unconfirmed email no longer refuses a login, so there is no "confirm your email"
            // branch here; EmailVerificationBanner does the prompting once they are inside.
            setError(result.message || "Invalid email or password");
        } catch {
            setError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Editing either field means they are trying again — the old error no longer applies.
    const editField = (setter: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setter(e.target.value);
        setError(null);
    };

    return (
        <div className="w-full max-w-[420px]">
            <h1 className="text-center font-display text-[28px] leading-[1.2] font-medium text-ink md:text-[34px] short:text-[27px] shorter:text-[24px]">
                <b className="font-semibold text-sidebar dark:text-leaf">Sign in</b> to your account
            </h1>
            <p className="mt-2 mb-6.5 text-center text-[13px] text-ink-muted md:mt-2.5 md:mb-7.5 md:text-sm short:mt-1.5 short:mb-4 shorter:mb-3">
                Development intelligence · trades · suppliers
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3.5 short:gap-2.5">
                {error && (
                    <div
                        className={`rounded-3xl px-5 py-4 text-sm ${typeof error === 'object'
                            ? 'border border-amber-200 bg-amber-50 text-ink dark:border-amber-700 dark:bg-amber-950/40'
                            : 'border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300'}`}
                        role="alert"
                    >
                        {error}
                    </div>
                )}

                <label className={pillFieldClass(email.length > 0)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-sidebar dark:text-leaf">
                        <rect x="2" y="4" width="20" height="16" rx="3" />
                        <path d="m22 7-10 6L2 7" />
                    </svg>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        autoComplete="email"
                        aria-label="Email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={editField(setEmail)}
                        className={`${pillInputClass} font-semibold`}
                    />
                </label>

                <label className={pillFieldClass(password.length > 0)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-ink-faint">
                        <rect x="3" y="11" width="18" height="11" rx="3" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        autoComplete="current-password"
                        aria-label="Password"
                        placeholder="Enter password"
                        value={password}
                        onChange={editField(setPassword)}
                        className={pillInputClass}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        className="shrink-0 cursor-pointer"
                    >
                        {showPassword ? (
                            <EyeIcon className="size-5 fill-ink-faint" />
                        ) : (
                            <EyeCloseIcon className="size-5 fill-ink-faint" />
                        )}
                    </button>
                </label>

                <div className={`flex items-center pr-1.5 text-[12.5px] md:text-[13px] ${isMobile ? 'justify-end' : 'justify-between'}`}>
                    {!isMobile && (
                        <label className="flex cursor-pointer items-center gap-2 text-ink-muted">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                className="size-4 accent-leaf"
                            />
                            Keep me signed in
                        </label>
                    )}
                    <Link to="/reset-password" className="font-semibold text-sidebar hover:underline dark:text-leaf">
                        Forgot your password?
                    </Link>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className={`mt-0.5 ${pillButtonClass}`}
                >
                    {loading ? "SIGNING IN…" : "LOGIN"}
                </button>

                <p className="mt-1 text-center text-[13px] text-ink-muted md:mt-1.5 md:text-[13.5px]">
                    Don&apos;t have an account?{" "}
                    <Link to="/signup" className="font-bold text-sidebar hover:underline dark:text-leaf">
                        Register now
                    </Link>
                </p>
            </form>
        </div>
    );
}
